import { Component, type ErrorInfo, type ReactNode } from "react";
import { IconAlertCircle } from "./icons";

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  State
> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info);
    this.setState({ error, info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
              <IconAlertCircle size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-800">
                Something broke{this.props.label ? ` in ${this.props.label}` : ""}
              </p>
              <p className="text-sm text-red-700 mt-0.5 font-mono whitespace-pre-wrap break-words">
                {this.state.error.message}
              </p>
              {this.state.info?.componentStack && (
                <details className="mt-2">
                  <summary className="text-xs text-red-600 cursor-pointer">
                    Component stack
                  </summary>
                  <pre className="text-[11px] text-red-700 mt-1 whitespace-pre-wrap break-words">
                    {this.state.info.componentStack}
                  </pre>
                </details>
              )}
              <button
                onClick={this.reset}
                className="mt-3 px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
